import {Client as AuthClient} from '@apps-common/auth';
import {AuthProvider} from '@apps-common/auth-react';
import {DocumentHandler} from '@apps-common/auth-utils';
import {setAuthToken as setGraneroAuthToken} from '@apps-common/geocorn-sdk/api';
import {setAuthToken} from '@apps-common/js-client/api/request';
import {CssBaseline, ThemeProvider} from '@mui/material';
import {useLDClient} from 'launchdarkly-react-client-sdk';
import React, {useCallback} from 'react';
import {Helmet} from 'react-helmet';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';

import Explorer from './Explorer.jsx';
import NoQuotaBanner from './no-quota-banner.jsx';
import {fetchBasemapRules} from '@explorer/actions/basemap-rules.js';
import {fetchItemTypes} from '@explorer/actions/item-types.js';
import {setLabStatus} from '@explorer/actions/labs.js';
import {fetchSeriesRequest} from '@explorer/actions/series.js';
import {fetchSubscriptions} from '@explorer/actions/subscriptions';
import {identify} from '@explorer/analytics.js';
import TopNavBar from '@explorer/components/navbars/top';
import {getUserLabKeys} from '@explorer/constants/labs.js';
import appGlobalStyles from '@explorer/styles/AppGlobalStyles.jsx';
import {theme} from '@explorer/styles/index.js';

const authClient = new AuthClient({
  clientId: import.meta.env.VITE_AUTH_CLIENT_ID,
  redirectUri: `${window.location.origin}${window.location.pathname}`,
  postLogoutRedirectUri: `${window.location.origin}${window.location.pathname}`,
});

const App = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const ldClient = useLDClient();

  const storedLabs = useSelector(state => state.labs);

  const navigate = url => {
    window.history.pushState({}, '', url);
  };

  /**
   * @type {import('@apps-common/auth/client.js').ChangeListener}
   */
  const onAuthChange = useCallback(
    state => {
      setAuthToken(state.user?.accessToken || '');
      setGraneroAuthToken(state.user?.accessToken || '');

      if (state.user && !state.pending) {
        const {
          email,
          organization_id,
          organization_name,
          program_id,
          role_level,
          user_id,
        } = state.user.claims;

        identify({
          email,
          organization_id,
          organization_name,
          program_id,
          role_level,
          user_id,
        });

        // See https://launchdarkly.github.io/js-client-sdk/interfaces/LDMultiKindContext.html
        ldClient.identify({
          kind: 'multi',
          user: {
            key: `user-${user_id}`,
            email,
            role_level,
            user_id,
            _meta: {
              privateAttributes: ['email'], //https://docs.launchdarkly.com/sdk/features/private-attributes
            },
          },
          organization: {
            key: `organization-${organization_id}`,
            organization_id,
            organization_name,
            program_id,
            _meta: {
              privateAttributes: ['organization_name'],
            },
          },
        });

        // this will block rendering until we have subscriptions
        dispatch(fetchSubscriptions());
        dispatch(fetchItemTypes());
        dispatch(fetchBasemapRules()).then(() => {
          dispatch(fetchSeriesRequest());
        });

        // Users may have labs enabled in localStorage that they shouldn't have access to
        // This can occur if they enabled the lab in a different account on the same device, or if they edited their local store
        // This checks permissions when the user logs in, and disables labs that shouldn't be available
        const userLabs = getUserLabKeys(state.user);

        for (const labKey in storedLabs) {
          if (!userLabs.includes(labKey)) {
            dispatch(setLabStatus({labKey, enabled: false}));
          }
        }
      }
    },
    [dispatch, ldClient, storedLabs]
  );

  return (
    <AuthProvider
      client={authClient}
      navigate={navigate}
      onChange={onAuthChange}
      private
    >
      <ThemeProvider defaultMode="dark" theme={theme}>
        <Helmet>
          <title>{t('common:meta.name')}</title>
          <meta itemProp="name" content={t('common:meta.name')} />
          <meta property="og:title" content={t('common:meta.name')} />
          <meta property="og:description" content={t('common:meta.tagline')} />
          <meta property="og:url" content={t('common:meta.url')} />
        </Helmet>
        <CssBaseline />
        {appGlobalStyles}
        <NoQuotaBanner />
        <TopNavBar />
        <DocumentHandler>
          <Explorer />
        </DocumentHandler>
      </ThemeProvider>
    </AuthProvider>
  );
};

export default App;
